import { all, call, put, takeLatest, delay } from "redux-saga/effects";
import axios from "axios";
import CryptoJS from "crypto-js";
import { BASE_URL } from "../../shared/constants";
import {
  searchByNameStart,
  searchByNameSuccess,
  searchByNameError,
  searchByCategoryStart,
  searchByCategorySuccess,
  searchByCategoryError,
  searchNearByStart,
  searchNearBySuccess,
  searchNearByError,
  searchByAreaStart,
  searchByAreaSuccess,
  searchByAreaError,
  suggestionFetchStart,
  suggestionFetchSuccess,
  suggestionFetchError,
  closeRoadFetchSuccess,
  closeRoadFetchError,
  closeRoadFetchStart,
} from "./reducer";

const MAP_VISUALIZATION_SECRET = process.env.REACT_APP_MAP_VISUALIZATION_SECRET;

const decryptBackendData = (responseData) => {
  if (!responseData || !responseData.data) {
    return responseData;
  }

  try {
    const bytes = CryptoJS.AES.decrypt(responseData.data, MAP_VISUALIZATION_SECRET);
    const decryptedText = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decryptedText);
  } catch (error) {
    console.error("Failed to decrypt backend payload:", error);
    return responseData;
  }
};

function* searchByName({ payload }) {
  try {
    yield delay(750);
    const response = yield axios.get(`${BASE_URL}/api/landmark/searchAllWeb`, {
      nameNew: payload.name,
      params: {
        name: payload.name,
        bbox: payload.bbox,
      },
    });

    const resultData = decryptBackendData(response.data);

    yield put(
      searchByNameSuccess({
        places: resultData?.landmarkResult || [],
        parcels: resultData?.parcelResult || [],
        buildings: resultData?.buildingResult || [],
        roads: resultData?.roadResult || [],
        neighbourhoods: resultData?.neighbourhoodResult || [],
      })
    );
  } catch (error) {
    yield put(
      searchByNameError({
        error,
      })
    );
  }
}

function* searchByCategory({ payload }) {
  try {
    const response = yield axios.get(`${BASE_URL}/api/landmark/searchByCategoryWeb`, {
      params: {
        category: payload.category,
        bbox: payload.bbox,
      },
    });

    const resultData = decryptBackendData(response.data);

    yield put(
      searchByCategorySuccess({
        places: resultData?.result || [],
      })
    );
  } catch (error) {
    yield put(
      searchByCategoryError({
        error,
      })
    );
  }
}

function* searchNearBy({ payload }) {
  try {
    let params = {
      placeName: payload.placeName,
    };

    if (payload.category) {
      params = {
        ...params,
        category: payload.category,
      };
    }
    const response = yield axios.get(`${BASE_URL}/api/landmark/searchNearByPlacesWeb`, {
      params,
    });

    const resultData = decryptBackendData(response.data);

    yield put(
      searchNearBySuccess({
        nearBy_places: resultData?.result || [],
      })
    );
  } catch (error) {
    yield put(
      searchNearByError({
        error,
      })
    );
  }
}

function* searchByArea({ payload }) {
  try {
    let params = {
      placeName: payload.placeName,
      units: payload.unit,
    };

    if (payload.category) {
      params = {
        ...params,
        category: payload.category,
      };
    }
    const response = yield axios.get(
      `${BASE_URL}/api/landmark/searchByServiceAreaWeb`,
      {
        params,
      }
    );

    const resultData = decryptBackendData(response.data);

    yield put(
      searchByAreaSuccess({
        places: Array.isArray(resultData?.placeNames) ? resultData.placeNames : [],
        centerPoint: payload.centerPoint,
      })
    );
  } catch (error) {
    yield put(
      searchByAreaError({
        error,
      })
    );
  }
}

function* fetchSuggestions({ payload }) {
  try {
    yield delay(750);
    const response = yield axios.get(`${BASE_URL}/api/landmark/searchAllWeb`, {
      params: {
        name: payload.name,
        bbox: payload.bbox,
      },
    });

    const { landmarkResult = [], parcelResult = [], roadResult = [], neighbourhoodResult = [] } =
      decryptBackendData(response.data) || {};

    const utf8Text = payload.name;
    const isNonEnglish = utf8Text.charCodeAt(1) > 4607;

    const landmarks = landmarkResult.map((place) => ({
      id: place.id,
      label: isNonEnglish ? place.properties?.legal_name : place.properties?.name_eng,
      city_name: place.properties?.city_name,
      region: place.properties?.region,

      coordinates: place.geometry?.coordinates,
    }));

    const parcels = parcelResult.map((parcel) => {
      let coordinates = undefined;
      if (parcel.geometry && parcel.geometry.coordinates && parcel.geometry.coordinates[0] && parcel.geometry.coordinates[0][0]) {
        const points = parcel.geometry.coordinates[0][0];
        if (points.length > 0) {
          let sumX = 0, sumY = 0;
          points.forEach(p => { sumX += p[0]; sumY += p[1]; });
          coordinates = [sumX / points.length, sumY / points.length];
        }
      }
      return {
        id: parcel.id,
        label: parcel.properties?.prcl_uid,
        city_name: parcel.properties?.city_name,
        region: parcel.properties?.region,
        coordinates,
      };
    });

    const roads = roadResult.map((road) => ({
      id: road.id,
      label: road.properties?.road_uid,
      coordinates: road.geometry?.coordinates[0],
    }));

    const neighbourhoods = neighbourhoodResult.map((neighbourhood) => ({
      id: neighbourhood.gid,
      label: neighbourhood.nh_code,
      coordinates: neighbourhood.geom?.coordinates[0][0][0],
    }));

    // Collect unique cities and regions
    const cities = new Set();
    const regions = new Set();

    // landmarkResult.forEach((place) => {
    //   if (place.properties?.city_name) cities.add(place.properties.city_name);
    //   if (place.properties?.region) regions.add(place.properties.region);
    // });

    // parcelResult.forEach((parcel) => {
    //   if (parcel.properties?.city_name) cities.add(parcel.properties.city_name);
    //   if (parcel.properties?.region) regions.add(parcel.properties.region);
    // });

    roadResult.forEach((road) => {
      if (road.properties?.city_name) cities.add(road.properties.city_name);
      if (road.properties?.region) regions.add(road.properties.region);
    });

    neighbourhoodResult.forEach((neighbourhood) => {
      if (neighbourhood.city_name) cities.add(neighbourhood.city_name);
      if (neighbourhood.region) regions.add(neighbourhood.region);
    });

    const citySuggestions = Array.from(cities).map((city) => ({
      id: `city-${city}`,
      label: city,
      type: 'city',
    }));

    const regionSuggestions = Array.from(regions).map((region) => ({
      id: `region-${region}`,
      label: region,
      type: 'region',
    }));

    yield put(
      suggestionFetchSuccess({
        suggestions: [
          ...landmarks,
          ...parcels,
          ...roads,
          ...neighbourhoods,
          ...citySuggestions,
          ...regionSuggestions,
        ],
      })
    );
  } catch (error) {
    yield put(
      suggestionFetchError({
        error,
      })
    );
  }
}

function* searchClosedRoad({ payload }) {
  try {
    const response = yield axios.get(`${BASE_URL}/api/roads/closed`);
    // console.log(response);
    yield put(
      closeRoadFetchSuccess({
        closedRoads: response.data,
      })
    );
  } catch (error) {
    yield put(
      closeRoadFetchError({
        error,
      })
    );
  }
}

function* onSearchByName() {
  yield takeLatest(searchByNameStart.type, searchByName);
}

function* onSearchByCategory() {
  yield takeLatest(searchByCategoryStart.type, searchByCategory);
}

function* onSearchNearBy() {
  yield takeLatest(searchNearByStart.type, searchNearBy);
}

function* onSearchByArea() {
  yield takeLatest(searchByAreaStart.type, searchByArea);
}

function* onFetchSuggestions() {
  yield takeLatest(suggestionFetchStart.type, fetchSuggestions);
}
function* onClosedRoad() {
  yield takeLatest(closeRoadFetchStart.type, searchClosedRoad);
}

function* mapSagas() {
  yield all([
    call(onSearchByName),
    call(onSearchByCategory),
    call(onSearchNearBy),
    call(onSearchByArea),
    call(onFetchSuggestions),
    call(onClosedRoad),
  ]);
}

export default mapSagas;
